/**
 * C&I battery energy storage (BESS) site layout: equipment library, placed equipment, site boundary, restricted
 * zones, preliminary geometric checks, equipment schedule and BOQ lines.
 *
 * Units: plan millimetres (same plan as the PV design: x right, y down). Footprints are rectangles rotated about their
 * centre; "front" is the local −y side (same convention as the PV equipment).
 *
 * The built-in library holds GENERIC entries only (no manufacturer, dimensions marked illustrative). Real products are
 * entered by the user from datasheets — the tool never invents product specifications. Checks are geometric
 * (overlaps, boundary, configured clearances, restricted zones); they are NOT electrical, fire-safety, HVAC,
 * structural or regulatory assessments.
 */
import { z } from 'zod';
import { polygonContains, polygonsOverlap, type Vec } from './geometry';
import type { RuleLine } from './parallel';

export const BESS_CATEGORIES = [
  'BATTERY_CABINET',
  'BATTERY_RACK',
  'CONTAINER',
  'PCS',
  'TRANSFORMER',
  'SWITCHGEAR',
  'DISTRIBUTION',
  'HVAC',
  'AUXILIARY',
] as const;
export type BessCategory = (typeof BESS_CATEGORIES)[number];
export const BESS_CATEGORY_LABEL: Record<BessCategory, string> = {
  BATTERY_CABINET: 'Tủ pin lưu trữ',
  BATTERY_RACK: 'Giá pin (rack)',
  CONTAINER: 'Container pin',
  PCS: 'Bộ chuyển đổi công suất (PCS)',
  TRANSFORMER: 'Máy biến áp',
  SWITCHGEAR: 'Thiết bị đóng cắt / tủ trung thế',
  DISTRIBUTION: 'Tủ phân phối',
  HVAC: 'Điều hòa / thông gió (HVAC)',
  AUXILIARY: 'Thiết bị phụ trợ',
};

export const BESS_DISCLAIMER =
  'Kiểm tra sơ bộ về hình học (chồng lấn, ranh giới, khoảng hở do dự án / nhà sản xuất khai báo, vùng cấm). Không thay thế thiết kế điện, PCCC, HVAC, kết cấu và thẩm định theo quy định.';

const pt = z.tuple([z.number().finite(), z.number().finite()]);
const id = z.string().min(1).max(40);
const dim = z.number().min(10).max(50_000).nullable().default(null);
const clr = z.number().min(0).max(20_000).default(0);

export const bessTypeSchema = z
  .object({
    id,
    category: z.enum(BESS_CATEGORIES),
    name: z.string().trim().min(1).max(120),
    manufacturer: z.string().max(120).nullable().default(null),
    model: z.string().max(120).nullable().default(null),
    /** Footprint along local x / y and height (mm); null = not known yet */
    lengthMm: dim,
    widthMm: dim,
    heightMm: dim,
    weightKg: z.number().min(0).max(200_000).nullable().default(null),
    /** Service clearances (mm) from the manufacturer documentation or the project */
    clearance: z.object({ front: clr, back: clr, left: clr, right: clr }).loose().default({ front: 0, back: 0, left: 0, right: 0 }),
    interfaces: z.string().max(300).default(''),
    /** Datasheet / manual reference */
    docRef: z.string().max(300).default(''),
    /** Dimensions are illustrative placeholders, not product data */
    illustrative: z.boolean().default(false),
    note: z.string().max(300).default(''),
  })
  .loose();
export type BessType = z.infer<typeof bessTypeSchema>;

export const bessItemSchema = z
  .object({
    id,
    typeId: z.string().min(1).max(40),
    label: z.string().trim().min(1).max(80),
    /** Plan centre (mm) */
    at: pt,
    rotationDeg: z.number().min(-360).max(360).default(0),
    groupId: z.string().max(40).nullable().default(null),
  })
  .loose();
export type BessItem = z.infer<typeof bessItemSchema>;

export const bessZoneSchema = z
  .object({ id, label: z.string().max(80).default(''), points: z.array(pt).min(3).max(200) })
  .loose();
export type BessZone = z.infer<typeof bessZoneSchema>;

export const bessDataSchema = z
  .object({
    version: z.literal(1).default(1),
    /** Site boundary polygon (plan mm); null = not drawn */
    boundary: z.array(pt).min(3).max(500).nullable().default(null),
    zones: z.array(bessZoneSchema).max(200).default([]),
    library: z.array(bessTypeSchema).max(300).default([]),
    items: z.array(bessItemSchema).max(2000).default([]),
  })
  .loose();
export type BessData = z.infer<typeof bessDataSchema>;

/** Generic starting entries — illustrative sizes, no manufacturer (replace with datasheet data). */
const GENERIC: [BessCategory, string, number, number, number][] = [
  ['BATTERY_CABINET', 'Tủ pin (mẫu chung)', 1200, 1400, 2300],
  ['BATTERY_RACK', 'Giá pin (mẫu chung)', 600, 800, 2000],
  ['CONTAINER', 'Container pin 20 ft (khổ ISO)', 6058, 2438, 2896],
  ['PCS', 'PCS (mẫu chung)', 1200, 800, 2200],
  ['TRANSFORMER', 'Máy biến áp (mẫu chung)', 2000, 1500, 2000],
  ['SWITCHGEAR', 'Tủ trung thế (mẫu chung)', 800, 1200, 2200],
  ['DISTRIBUTION', 'Tủ phân phối (mẫu chung)', 800, 600, 2000],
  ['HVAC', 'Dàn HVAC (mẫu chung)', 1000, 600, 1500],
  ['AUXILIARY', 'Thiết bị phụ trợ (mẫu chung)', 600, 600, 1800],
];
export function genericBessLibrary(): BessType[] {
  return GENERIC.map(([category, name, l, w, h]) =>
    bessTypeSchema.parse({
      id: `GEN-${category}`,
      category,
      name,
      lengthMm: l,
      widthMm: w,
      heightMm: h,
      illustrative: true,
      note: 'Kích thước minh hoạ — thay bằng số liệu datasheet của thiết bị thực tế.',
    }),
  );
}
export const emptyBess = (): BessData => bessDataSchema.parse({ version: 1, library: genericBessLibrary() });

// ---------------------------------------------------------------------------------------------- geometry

type P = [number, number];
const V = (p: P): Vec => ({ x: p[0], y: p[1] });

/** Footprint & clearance zone (plan mm) of a placed item; null size ⇒ 1 m placeholder flagged by the checks. */
export function bessGeometry(item: Pick<BessItem, 'at' | 'rotationDeg'>, type: BessType | undefined) {
  const L = type?.lengthMm ?? 1000;
  const W = type?.widthMm ?? 1000;
  const c = type?.clearance ?? { front: 0, back: 0, left: 0, right: 0 };
  const t = (item.rotationDeg * Math.PI) / 180;
  const cos = Math.cos(t);
  const sin = Math.sin(t);
  const w = ([x, y]: P): P => [item.at[0] + x * cos - y * sin, item.at[1] + x * sin + y * cos];
  const rect = (x0: number, y0: number, x1: number, y1: number): P[] => [w([x0, y0]), w([x1, y0]), w([x1, y1]), w([x0, y1])];
  const footprint = rect(-L / 2, -W / 2, L / 2, W / 2);
  const hasZone = c.front + c.back + c.left + c.right > 0;
  // Front = local −y
  const zone = hasZone ? rect(-L / 2 - c.left, -W / 2 - c.front, L / 2 + c.right, W / 2 + c.back) : [];
  return { footprint, zone, sized: !!(type?.lengthMm && type?.widthMm), heightMm: type?.heightMm ?? 1000 };
}

export interface BessIssue {
  severity: 'error' | 'warning' | 'info';
  code: string;
  message: string;
  ids: string[];
}

/** Shrink a polygon by ~1 mm towards its centroid: touching neighbours / boundaries are not conflicts. */
const shrink = (a: P[]): Vec[] => {
  const cx = a.reduce((t, q) => t + q[0], 0) / a.length;
  const cy = a.reduce((t, q) => t + q[1], 0) / a.length;
  return a.map(([x, y]) => {
    const l = Math.hypot(x - cx, y - cy) || 1;
    return { x: x - ((x - cx) / l) * 1, y: y - ((y - cy) / l) * 1 };
  });
};
const overlap = (a: P[], b: P[]) => polygonsOverlap(shrink(a), shrink(b));
const inside = (poly: P[], q: P[]) => polygonContains(poly.map(V), shrink(q));

/** Preliminary geometric checks of the BESS layout. */
export function bessChecks(d: BessData): BessIssue[] {
  const out: BessIssue[] = [];
  const lib = new Map(d.library.map((t) => [t.id, t]));
  const geo = d.items.map((it) => ({ it, type: lib.get(it.typeId), g: bessGeometry(it, lib.get(it.typeId)) }));
  const unknown = geo.filter((x) => !x.type);
  if (unknown.length)
    out.push({ severity: 'error', code: 'UNKNOWN_TYPE', message: `${unknown.length} thiết bị không còn mẫu trong thư viện.`, ids: unknown.map((x) => x.it.id) });
  const noDim = geo.filter((x) => x.type && !(x.type.lengthMm && x.type.widthMm && x.type.heightMm));
  if (noDim.length)
    out.push({
      severity: 'error',
      code: 'MISSING_DIMENSIONS',
      message: `Thiếu kích thước thiết bị (vẽ tạm 1 m): ${[...new Set(noDim.map((x) => x.type!.name))].join(', ')}.`,
      ids: noDim.map((x) => x.it.id),
    });
  const illus = geo.filter((x) => x.type?.illustrative);
  if (illus.length)
    out.push({
      severity: 'info',
      code: 'ILLUSTRATIVE_DIMENSIONS',
      message: `${illus.length} thiết bị dùng kích thước minh hoạ của mẫu chung — thay bằng số liệu datasheet.`,
      ids: illus.map((x) => x.it.id),
    });
  const noClr = geo.filter((x) => x.type && x.g.zone.length === 0);
  if (noClr.length)
    out.push({
      severity: 'info',
      code: 'NO_CLEARANCE',
      message: `${noClr.length} thiết bị chưa khai báo khoảng hở bảo trì — chưa kiểm tra vùng thao tác.`,
      ids: noClr.map((x) => x.it.id),
    });
  // Overlaps & clearance zones blocked
  const overlaps: string[] = [];
  const blocked: string[] = [];
  for (let i = 0; i < geo.length; i++)
    for (let j = 0; j < geo.length; j++) {
      if (i === j) continue;
      const a = geo[i]!;
      const b = geo[j]!;
      if (i < j && overlap(a.g.footprint, b.g.footprint)) overlaps.push(`${a.it.label}/${b.it.label}`);
      if (a.g.zone.length && overlap(a.g.zone, b.g.footprint) && !overlap(a.g.footprint, b.g.footprint)) blocked.push(a.it.id);
    }
  if (overlaps.length)
    out.push({
      severity: 'error',
      code: 'OVERLAP',
      message: `Thiết bị chồng lấn: ${overlaps.slice(0, 8).join(', ')}${overlaps.length > 8 ? '…' : ''}.`,
      ids: geo.filter((a) => geo.some((b) => a !== b && overlap(a.g.footprint, b.g.footprint))).map((x) => x.it.id),
    });
  if (blocked.length)
    out.push({
      severity: 'warning',
      code: 'CLEARANCE_BLOCKED',
      message: `Vùng khoảng hở bảo trì bị thiết bị khác chiếm: ${[...new Set(blocked)].map((i) => d.items.find((x) => x.id === i)!.label).slice(0, 8).join(', ')}.`,
      ids: [...new Set(blocked)],
    });
  // Site boundary & restricted zones
  if (d.boundary) {
    const b = d.boundary as P[];
    const outside = geo.filter((x) => !inside(b, x.g.footprint));
    if (outside.length)
      out.push({ severity: 'error', code: 'OUTSIDE_SITE', message: `${outside.length} thiết bị nằm ngoài ranh giới khu đất.`, ids: outside.map((x) => x.it.id) });
    const zoneOut = geo.filter((x) => x.g.zone.length && inside(b, x.g.footprint) && !inside(b, x.g.zone));
    if (zoneOut.length)
      out.push({ severity: 'warning', code: 'CLEARANCE_OUTSIDE_SITE', message: `${zoneOut.length} thiết bị có vùng khoảng hở vượt ra ngoài ranh giới khu đất.`, ids: zoneOut.map((x) => x.it.id) });
  } else if (d.items.length)
    out.push({ severity: 'info', code: 'NO_BOUNDARY', message: 'Chưa vẽ ranh giới khu đất — chưa kiểm tra vị trí so với ranh giới.', ids: [] });
  const inZone = geo.filter((x) => d.zones.some((z) => overlap(z.points as P[], x.g.footprint)));
  if (inZone.length)
    out.push({ severity: 'error', code: 'IN_RESTRICTED_ZONE', message: `${inZone.length} thiết bị nằm trong vùng hạn chế.`, ids: inZone.map((x) => x.it.id) });
  return out;
}

export interface BessScheduleRow {
  typeId: string;
  category: BessCategory | null;
  name: string;
  manufacturer: string | null;
  model: string | null;
  quantity: number;
  sizeMm: string;
  unitWeightKg: number | null;
  totalWeightKg: number | null;
  illustrative: boolean;
  labels: string[];
}

/** Equipment schedule grouped by library entry (from the placed equipment). */
export function bessSchedule(d: BessData): BessScheduleRow[] {
  const lib = new Map(d.library.map((t) => [t.id, t]));
  const rows = new Map<string, BessScheduleRow>();
  for (const it of d.items) {
    const t = lib.get(it.typeId);
    let r = rows.get(it.typeId);
    if (!r) {
      const size = t?.lengthMm && t.widthMm && t.heightMm ? `${t.lengthMm}×${t.widthMm}×${t.heightMm}` : 'chưa có';
      r = {
        typeId: it.typeId,
        category: t?.category ?? null,
        name: t?.name ?? `(không còn mẫu ${it.typeId})`,
        manufacturer: t?.manufacturer ?? null,
        model: t?.model ?? null,
        quantity: 0,
        sizeMm: size,
        unitWeightKg: t?.weightKg ?? null,
        totalWeightKg: null,
        illustrative: !!t?.illustrative,
        labels: [],
      };
      rows.set(it.typeId, r);
    }
    r.quantity++;
    r.labels.push(it.label);
  }
  for (const r of rows.values()) r.totalWeightKg = r.unitWeightKg === null ? null : r.unitWeightKg * r.quantity;
  return [...rows.values()].sort(
    (a, b) => BESS_CATEGORIES.indexOf(a.category ?? 'AUXILIARY') - BESS_CATEGORIES.indexOf(b.category ?? 'AUXILIARY') || a.name.localeCompare(b.name),
  );
}

/** BOQ lines (equipment counted from the layout; no catalogue product linked, awaiting approval). */
export function bessRuleLines(d: BessData): RuleLine[] {
  return bessSchedule(d).map((r) => ({
    key: `BESS:${r.typeId}`,
    category: 'OTHER',
    ruleId: 'BESS.LAYOUT@v1',
    productId: null,
    label: `${r.category ? BESS_CATEGORY_LABEL[r.category] : 'Thiết bị BESS'} — ${[r.manufacturer, r.model].filter(Boolean).join(' ') || r.name}`,
    unit: 'cái',
    quantity: r.quantity,
    spec: r.sizeMm,
    materialKey: `BESS_${r.category ?? 'OTHER'}`,
    basis: [
      { label: 'Đếm từ bản bố trí BESS', value: r.labels.slice(0, 20).join(', ') },
      ...(r.illustrative ? [{ label: 'Kích thước', value: 'minh hoạ (mẫu chung)' }] : []),
    ],
    unverified: true,
  }));
}
